import {cloneDeep, get, has, isEqual, isFinite} from "lodash";
import memoizeOne from "memoize-one";
import moment from "moment";
import PropTypes from "prop-types";
import React from "react";
import {Modal} from "react-bootstrap";
import {withTranslation} from "react-i18next";
import {connect} from "react-redux";
import {bindActionCreators, compose} from "redux";
import {Field, formValueSelector, isSubmitting, reduxForm} from "redux-form";
import {connectModal, show as showModal} from "redux-modal";
import Paths from "../../../api/paths";
import DevType from "@wesstron/utils/Api/constants/devTypes";
import {
    CurveDayShowingType,
    CurveTypesWithInsemination,
    FeedingDoseCorrection,
    FeedingType,
    NRFWorkType,
} from "../../../constans/feedingTypes";
import {invokeApig} from "../../../libs/awsLib";
import {getFeedingCurves} from "../../../selectors/feedingSelector";
import {
    changeCurveInitialize,
    formatCurveDay,
    getAvailableCurveTypes,
    getCurveAndDayForAPig,
    getCurveDayShowingType,
    getReceiversType,
    isValidCurveForDevType,
} from "../../../utils/FeedingUtils";
import {getFeedingUnit} from "../../../utils/SettingsUtils";
import {convertWeightUnitTo, getUnit} from "../../../utils/UnitUtils";
import Chart from "../../basics/chart/Chart";
import ReduxLabeledInput from "../../basics/input/labeled-input/ReduxLabeledInput";
import ReduxLabeledSelect from "../../basics/select/labeled-select/ReduxLabeledSelect";
import LoadingComponent from "../../loading/LoadingComponent";
import ReduxLabeledSliderToSelect from "../../redux-mobile-input-swapper/ReduxLabeledSliderToSelect";
import ModalBody from "../ModalBody";
import ModalFooter from "../ModalFooter";
import ModalHeader from "../ModalHeader";
import {submit, validate} from "./FeedingManageFeedingSubmit";
import Button from "../../basics/button/Button";
import {reset} from "redux-form";
import {checkIfUserHasRole} from "../../../utils/NewRolesUtils";
import RoleTypes from "@wesstron/utils/Api/constants/roleTypes";

export const ModalName = "feeding-manage-feeding-modal";

// Komponent z logiką formularza - czysty, bez Redux Form
class FeedingManageFeedingForm extends React.Component {
    constructor(props) {
        super(props);
        const {selectedNodes, initialize, curves, sectorType, farm} =
            this.props;
        this.isInitializedWithRealData = false;
        this.unit = getFeedingUnit();
        this.state = {
            pigEvents: [],
            loading: null,
        };
        if (selectedNodes?.length) {
            const curveId = get(selectedNodes[0], "curve.id");
            const availableCurveTypes = curves.filter((o) =>
                getAvailableCurveTypes({sectorType}).includes(o.SetData.Type)
            );
            const curve =
                availableCurveTypes.find((o) => o.SetID === curveId) ||
                (availableCurveTypes.length === 1
                    ? availableCurveTypes[0]
                    : null);
            const isTriggerWorkType = !!selectedNodes.filter(
                (o) => o.workType === NRFWorkType.TRIGGER
            ).length;
            const showingType = getCurveDayShowingType(curve);
            const curveDay = get(selectedNodes[0], "curve.day", 1);
            const repeatDays = showingType.NORMAL
                ? get(selectedNodes[0], "loop.endDay", 0)
                : get(selectedNodes[0], "loop.endDay", 0) +
                  get(selectedNodes[0], "loop.insemination", 0);
            let repetitionDate;
            let repetitionDay = get(curve, "SetData.InseminationJumpTo", 0);
            let curveSize = get(curve, "SetData.Days.length", 0);
            const curveStart = moment()
                .startOf("day")
                .subtract(curveDay + repeatDays - 1, "day")
                .format("YYYY-MM-DD");

            if (selectedNodes.length === 1 && selectedNodes[0].animal) {
                this.state.loading = true;
                const AnimalID = selectedNodes[0].animal.AnmID;
                invokeApig({
                    ...Paths.listEventsForAnimals(),
                    body: {
                        AnmIDs: [AnimalID],
                    },
                    queryParams: {
                        FarmID: farm,
                    },
                })
                    .then((r) =>
                        this.setState({
                            loading: false,
                            pigEvents: r[AnimalID] || [],
                        })
                    )
                    .finally(() => {
                        this.setState({
                            loading: false,
                        });
                    });
            }
            switch (showingType) {
                case CurveDayShowingType.BEFORE: {
                    repetitionDate = moment()
                        .startOf("day")
                        .subtract(curveDay + repeatDays - 1 - curveSize, "day")
                        .format("YYYY-MM-DD");
                    break;
                }
                case CurveDayShowingType.AFTER: {
                    repetitionDate = curveStart;
                    break;
                }
                case CurveDayShowingType.BOTH: {
                    repetitionDate = moment()
                        .startOf("day")
                        .subtract(
                            curveDay +
                                repeatDays -
                                1 -
                                Math.max(0, repetitionDay - 1),
                            "day"
                        )
                        .format("YYYY-MM-DD");
                    break;
                }
                default:
                    repetitionDate = curveStart;
                    break;
            }
            const initialValues = {
                curve,
                curveShift: get(selectedNodes[0], "curve.offset", 0),
                curveDay: isFinite(curveDay) ? curveDay : 1,
                repetitionDate: repetitionDate,
                doseCorrection: get(selectedNodes[0], "curve.correction", 0),
                punishment: isTriggerWorkType
                    ? get(selectedNodes[0], "curve.punishment", 0)
                    : 0,
            };
            this.isInitializedWithRealData =
                selectedNodes.length === 1
                    ? has(selectedNodes[0], "curve.day") &&
                      curveId === initialValues.curve?.SetID
                    : false;
            if (props.onInitializedChange) {
                props.onInitializedChange(this.isInitializedWithRealData);
            }
            initialize(initialValues);
            this.isCurveSetByEvents = false;
            this.isResetting = false;
        }
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        const {curve, change, dirty, initialized} = this.props;
        const {pigEvents, loading} = this.state;
        const justInitialized = !prevProps.initialized && !!initialized;
        if (justInitialized || !prevProps.initialized) return;

        // Jeśli właśnie resetujemy, zignoruj zmiany krzywej
        if (this.isResetting) {
            this.isResetting = false;
            return;
        }

        const curveChanged =
            prevProps.curve !== undefined && !isEqual(prevProps.curve, curve);
        if (
            !curve ||
            (this.isCurveSetByEvents &&
                !!prevProps.curve &&
                !isEqual(prevProps.curve, curve))
        ) {
            console.log("reset 'isCurveSetByEvents' flag");
            this.isCurveSetByEvents = false;
        }
        const pigEventsChanged =
            !curve &&
            !dirty &&
            (!isEqual(prevState.pigEvents, pigEvents) ||
                loading === null ||
                (loading === false && loading !== prevState.loading));

        if (curveChanged && !this.isCurveSetByEvents) {
            console.log("change curve (by init)");
            const [key, value] = Object.entries(
                changeCurveInitialize(curve, pigEvents)
            )[0];
            change(key, value);
        } else if (pigEventsChanged) {
            console.log("change curve (by events)");
            const {type, curves, sectorType, receivers} = this.props;
            const isWST = !!(getReceiversType(receivers) & 0b01);
            const defaultCurve = getCurveAndDayForAPig(
                sectorType,
                pigEvents,
                curves,
                type === FeedingType.INDIVIDUAL,
                isWST
            );
            if (defaultCurve) {
                console.log("set 'isCurveSetByEvents' flag");
                this.isCurveSetByEvents = true;
                for (let [key, value] of Object.entries(defaultCurve)) {
                    change(key, value);
                }
            }
        }
    }

    getCurveDay = () => {
        const {repetitionDate, curveDay, curve} = this.props;
        const showingType = getCurveDayShowingType(curve);
        const curveLen = get(curve, "SetData.Days.length", 0);
        const repetitionDay = get(curve, "SetData.InseminationJumpTo", 0);
        switch (showingType) {
            case CurveDayShowingType.BOTH:
                return repetitionDate
                    ? moment()
                          .startOf("day")
                          .diff(
                              moment(repetitionDate, "YYYY-MM-DD")
                                  .subtract(repetitionDay, "day")
                                  .startOf("day"),
                              "day"
                          )
                    : 1;
            case CurveDayShowingType.AFTER:
                return repetitionDate
                    ? moment()
                          .startOf("day")
                          .diff(
                              moment(repetitionDate, "YYYY-MM-DD").startOf(
                                  "day"
                              ),
                              "day"
                          ) + 1
                    : 1;
            case CurveDayShowingType.BEFORE:
                return repetitionDate
                    ? moment()
                          .startOf("day")
                          .diff(
                              moment(repetitionDate, "YYYY-MM-DD")
                                  .subtract(curveLen, "day")
                                  .startOf("day"),
                              "day"
                          ) + 1
                    : 1;
            case CurveDayShowingType.NORMAL:
            default:
                return isFinite(curveDay)
                    ? Math.max(Math.min(curveDay, curveLen || 1), 1)
                    : 1;
        }
    };

    getCurveOptions = memoizeOne((curves, sectorType, isCompatibilityMode) => {
        return curves
            .filter(
                (c) =>
                    getAvailableCurveTypes({sectorType}).includes(
                        c.SetData.Type
                    ) &&
                    (!isCompatibilityMode ||
                        isValidCurveForDevType({
                            curve: c,
                            devType: DevType.DISPENSER,
                        }))
            )
            .sort((a, b) => a.SetData.Index - b.SetData.Index)
            .map((c) => {
                return {
                    name: c.SetData.Name,
                    value: cloneDeep(c),
                };
            });
    });

    calculateCurveDayFromDate = (date, curve, repetitionDate, curveDay) => {
        const showingType = getCurveDayShowingType(curve);
        const curveLen = get(curve, "SetData.Days.length", 0);
        const repetitionDay = get(curve, "SetData.InseminationJumpTo", 0);

        const dateObj = moment(date, "DD.MM.YYYY");
        if (!dateObj.isValid()) return null;

        let startDate;
        switch (showingType) {
            case CurveDayShowingType.BOTH:
                if (repetitionDate) {
                    startDate = moment(repetitionDate, "YYYY-MM-DD").subtract(
                        repetitionDay,
                        "day"
                    );
                } else {
                    startDate = moment()
                        .startOf("day")
                        .subtract(curveDay + repetitionDay - 1, "day");
                }
                break;
            case CurveDayShowingType.AFTER:
                if (repetitionDate) {
                    startDate = moment(repetitionDate, "YYYY-MM-DD");
                } else {
                    startDate = moment()
                        .startOf("day")
                        .subtract(curveDay - 1, "day");
                }
                break;
            case CurveDayShowingType.BEFORE:
                if (repetitionDate) {
                    startDate = moment(repetitionDate, "YYYY-MM-DD").subtract(
                        curveLen,
                        "day"
                    );
                } else {
                    startDate = moment()
                        .startOf("day")
                        .subtract(curveDay + curveLen - 1, "day");
                }
                break;
            case CurveDayShowingType.NORMAL:
            default:
                const calculatedCurveDay = isFinite(curveDay)
                    ? Math.max(Math.min(curveDay, curveLen || 1), 1)
                    : 1;
                startDate = moment()
                    .startOf("day")
                    .subtract(calculatedCurveDay - 1, "day");
                break;
        }

        const dayDiff = dateObj
            .startOf("day")
            .diff(startDate.startOf("day"), "days");
        const calculatedDay = dayDiff;
        return calculatedDay > 0 && calculatedDay <= curveLen
            ? calculatedDay
            : null;
    };

    mergeChartData = (
        curveDays,
        feedingChartData,
        curve,
        repetitionDate,
        curveDay
    ) => {
        if (!this.hasFeedingChartData()) {
            return curveDays.map((o, i) => ({
                ...o,
                name: `${formatCurveDay(i + 1, curve)}`,
            }));
        }

        const consumptionMap = new Map();
        feedingChartData.forEach((item) => {
            const date = item.name;
            if (date) {
                const calculatedCurveDay = this.calculateCurveDayFromDate(
                    date,
                    curve,
                    repetitionDate,
                    curveDay
                );
                if (calculatedCurveDay) {
                    const consumption = item.usage || 0;
                    const existing =
                        consumptionMap.get(calculatedCurveDay) || 0;
                    consumptionMap.set(
                        calculatedCurveDay,
                        existing + consumption
                    );
                }
            }
        });

        return curveDays.map((o, i) => {
            const dayNumber = i + 1;
            return {
                ...o,
                name: `${formatCurveDay(dayNumber, curve)}`,
                Consumption: consumptionMap.get(dayNumber) || null,
            };
        });
    };

    onButtonClick = () => {
        const {handleSubmit, setIsReadonly} = this.props;
        handleSubmit();
        if (setIsReadonly) setIsReadonly(true);
    };

    toggleEdit = () => {
        const {setIsReadonly, readOnly, dispatch} = this.props;
        if (!readOnly) {
            this.isResetting = true;
            dispatch(reset(ModalName));
        }
        setIsReadonly(!readOnly);
    };
    getUnit = () => {
        return getUnit("weight", this.unit);
    };

    formatWeight = (value) =>
        convertWeightUnitTo(value, {
            showUnit: false,
            rawValue: true,
            acceptNil: true,
            unit: this.unit,
            fixed: this.unit ? 2 : 0,
            defaultValue: 0,
        });
    hasFeedingChartData = () => {
        const {feedingChartData} = this.props;
        return feedingChartData && feedingChartData.length;
    };
    render() {
        const {
            initialized,
            sectorType,
            selectedNodes,
            receivers,
            curves,
            curve,
            repetitionDate,
            type,
            t,
            fullscreen = true,
            readOnly = false,
            showSubmitButton = false,
            submitting,
            feedingChartData,
            invalid,
            dirty,
        } = this.props;

        let curveType = get(curve, "SetData.Type");
        const isTriggerWorkType = selectedNodes
            ? selectedNodes.some((o) => o.workType === NRFWorkType.TRIGGER)
            : false;
        const showingType = getCurveDayShowingType(curve);
        const isCompatibilityMode = !!(getReceiversType(receivers) & 0b01);
        const curvesOptions = this.getCurveOptions(
            curves,
            sectorType,
            isCompatibilityMode
        );
        const days = get(curve, "SetData.Days", []);
        let curveDay = this.getCurveDay();
        curveDay = Math.min(Math.max(curveDay, 1), days.length);
        const isBefore = moment(repetitionDate, "YYYY-MM-DD")
            .startOf("day")
            .isSameOrBefore(moment().startOf("day"));
        const repetitionDay = get(curve, "SetData.InseminationJumpTo", -151);
        const unit = getFeedingUnit();

        const mergedData = this.mergeChartData(
            days,
            feedingChartData,
            curve,
            repetitionDate,
            curveDay
        );

        const chartDef = [];

        if (this.hasFeedingChartData()) {
            chartDef.push({
                color: "#ffa000",
                dataKey: "Consumption",
                chartType: "Bar",
                name: t("settings.fedByCurve"),
                unit: this.getUnit(),
                valueConverter: this.formatWeight,
                opacity: 1,
                stack: "feed",
            });
        }

        chartDef.push(
            {
                color: "green",
                dataKey: "ForageAmount",
                name: t("dosage"),
                unit: getUnit("weight", unit),
                valueConverter: (value) =>
                    +convertWeightUnitTo(value, {
                        unit: unit,
                        showUnit: false,
                        fixed: 2,
                    }),
            },
            {
                color: "red",
                dataKey: "currentCurveDay",
                name: t("currentCurveDay"),
            }
        );
        if (getCurveDayShowingType(curve) === CurveDayShowingType.BOTH) {
            chartDef.push({
                color: "blue",
                dataKey: CurveTypesWithInsemination.includes(curveType)
                    ? t("inseminationDay")
                    : t("parturitionDate"),
                name: CurveTypesWithInsemination.includes(curveType)
                    ? t("inseminationDay")
                    : t("parturitionDate"),
            });
        }
        if (days.some((day) => day.Notify)) {
            chartDef.push({
                color: "blue",
                opacity: 1,
                dataKey: "Notify",
                chartType: "Scatter",
                name: t("deviceRows.feeding.dispenserRow.condition"),
                valueConverter: (value) => (value ? 0 : null),
            });
        }
        let referenceLines = [
            {
                x: `${formatCurveDay(curveDay, curve)}`,
                color: "red",
                name: t("currentCurveDay"),
                hideIndex: 1,
            },
            getCurveDayShowingType(curve) === CurveDayShowingType.BOTH
                ? {
                      x: `${formatCurveDay(repetitionDay, curve)}`,
                      color: "blue",
                      position: "middle",
                      name: CurveTypesWithInsemination.includes(curveType)
                          ? t("inseminationDay")
                          : t("parturitionDate"),
                      hideIndex: 2,
                  }
                : null,
        ].filter((o) => !!o);

        // kiedy linie wypadaja na jednym X - polacz je w jedno
        if (
            referenceLines.length === 2 &&
            referenceLines[0].x === referenceLines[1].x
        ) {
            referenceLines = [
                {
                    ...referenceLines[0],
                    name: `${referenceLines[0].name} + ${referenceLines[1].name}`,
                    position: "middle",
                },
            ];
            chartDef.splice(-1, 1);
            chartDef[chartDef.length - 1].name = referenceLines[0].name;
        }

        const canEdit = checkIfUserHasRole(RoleTypes.DEVICE_FEEDING_WRITE);
        return (
            <div className={"mh-5rem"}>
                {canEdit && showSubmitButton && (
                    <div
                        className="buttons"
                        style={{
                            position: "absolute",
                            top: "13px",
                            right: "90px",
                        }}>
                        {!readOnly && (
                            <Button
                                disabled={submitting || !dirty || invalid}
                                buttonColor="info"
                                onClick={this.onButtonClick}>
                                {t("save")}
                            </Button>
                        )}
                        <Button
                            onClick={this.toggleEdit}
                            buttonStyle={"text"}
                            buttonColor={readOnly ? "info" : ""}>
                            {readOnly ? (
                                <i className="fa-solid fa-gear" />
                            ) : (
                                <i className="fa-solid fa-xmark" />
                            )}
                        </Button>
                    </div>
                )}
                {!initialized && <LoadingComponent isLoading={!initialized} />}
                {!!initialized && (
                    <>
                        <div style={{height: "20rem"}}>
                            <Chart
                                dataDef={chartDef}
                                tooltipLabelFormatter={(val) =>
                                    `${t("curveDay")} ${val}`
                                }
                                data={mergedData}
                                Yaxis={{
                                    name: t("dosage"),
                                }}
                                Xaxis={{
                                    name: t("curveDay"),
                                    dataKey: "name",
                                }}
                                type={"Composed"}
                                tooltipFormatter={(value) =>
                                    value === 0 ? t("yes") : value
                                }
                                referenceLines={referenceLines}
                                enableFullscreen={fullscreen}
                            />
                        </div>

                        <Field
                            name="curve"
                            id="curve"
                            required
                            component={ReduxLabeledSelect}
                            options={curvesOptions}
                            label={t("feedingCurve")}
                            disabled={readOnly}
                        />
                        {showingType === CurveDayShowingType.NORMAL && (
                            <>
                                <Field
                                    name="curveDay"
                                    label={t("currentCurveDay")}
                                    id="curveDay"
                                    required
                                    type={"number"}
                                    format={(value) => `${value}`}
                                    parse={(value) =>
                                        isFinite(parseInt(value))
                                            ? parseInt(value)
                                            : value
                                    }
                                    component={ReduxLabeledInput}
                                    disabled={readOnly}
                                />
                            </>
                        )}
                        {showingType !== CurveDayShowingType.NORMAL && (
                            <Field
                                name="repetitionDate"
                                label={
                                    !isBefore
                                        ? CurveTypesWithInsemination.includes(
                                              curveType
                                          )
                                            ? t(
                                                  "modals.feedingManageFeedingModal.plannedInseminationDate"
                                              )
                                            : t(
                                                  "modals.feedingManageFeedingModal.plannedParturitionDate"
                                              )
                                        : CurveTypesWithInsemination.includes(
                                                curveType
                                            )
                                          ? t(
                                                "modals.feedingManageFeedingModal.inseminationDate"
                                            )
                                          : t(
                                                "modals.feedingManageFeedingModal.parturitionDate"
                                            )
                                }
                                id="repetitionDate"
                                type={"date"}
                                required
                                component={ReduxLabeledInput}
                                disabled={readOnly}
                            />
                        )}
                        {[FeedingType.INDIVIDUAL].includes(type) &&
                            isTriggerWorkType && (
                                <Field
                                    name="punishment"
                                    id="punishment"
                                    type="number"
                                    required
                                    step={1}
                                    min={0}
                                    max={3}
                                    component={ReduxLabeledSliderToSelect}
                                    valueFormatter={(value) =>
                                        value
                                            ? `${value}`
                                            : t(
                                                  "modals.feedingManageFeedingModal.noPunishment"
                                              )
                                    }
                                    label={t("punishment")}
                                    disabled={readOnly}
                                />
                            )}
                        <Field
                            name="doseCorrection"
                            id="doseCorrection"
                            type="number"
                            required
                            step={FeedingDoseCorrection.STEP}
                            min={FeedingDoseCorrection.MIN}
                            max={FeedingDoseCorrection.MAX}
                            component={ReduxLabeledSliderToSelect}
                            valueFormatter={(value) => `${value}%`}
                            label={t(
                                "modals.feedingManageFeedingModal.doseCorrection"
                            )}
                            disabled={readOnly}
                        />
                    </>
                )}
            </div>
        );
    }
}

FeedingManageFeedingForm.propTypes = {
    selectedNodes: PropTypes.array.isRequired,
    feedingChartData: PropTypes.array,
};

// Eksport formy z Redux (bez reduxForm) - do użycia standalone
const formSelector = formValueSelector(ModalName);
const mapFormStateToProps = (state) => {
    return {
        curves: getFeedingCurves(state),
        farm: state.location.farm,
        curve: formSelector(state, "curve"),
        curveDay: formSelector(state, "curveDay"),
        repetitionDate: formSelector(state, "repetitionDate"),
        submitting: isSubmitting(ModalName),
    };
};

export const FeedingManageFeedingFormConnected = compose(
    withTranslation(),
    connect(mapFormStateToProps, (dispatch) =>
        bindActionCreators({showModal}, dispatch)
    ),
    reduxForm({
        form: ModalName,
        onSubmit: submit,
        validate,
        validateOnBlur: true,
        initialValues: {
            doseCorrection: 0,
            curveDay: 1,
            curveShift: 0,
            curve: undefined,
            punishment: 0,
        },
    })
)(FeedingManageFeedingForm);

// Komponent modala - tylko logika wyświetlania
class FeedingManageFeedingModal extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            isInitializedWithRealData: false,
        };
    }
    handleInitializedChange = (value) => {
        this.setState({isInitializedWithRealData: value});
    };
    render() {
        const {show, handleHide, handleSubmit, pristine, t} = this.props;

        return (
            <Modal onHide={handleHide} show={show} size={"lg"}>
                <form onSubmit={handleSubmit}>
                    <ModalHeader
                        title={t(
                            "modals.feedingManageFeedingModal.manageFeeding"
                        )}
                        onCloseClick={handleHide}
                    />
                    <ModalBody className={ModalName}>
                        <FeedingManageFeedingForm
                            {...this.props}
                            onInitializedChange={this.handleInitializedChange}
                        />
                    </ModalBody>
                    <ModalFooter
                        hasConfirmButton={true}
                        confirmText={t("modals.feedingManageFeedingModal.set")}
                        onCloseClick={handleHide}
                        formName={ModalName}
                        hasButtonDisabled={
                            this.state.isInitializedWithRealData && pristine
                        }
                    />
                </form>
            </Modal>
        );
    }
}

FeedingManageFeedingModal.propTypes = {
    selectedNodes: PropTypes.array.isRequired,
};

const selector = formValueSelector(ModalName);
const mapStateToProps = (state) => {
    return {
        curves: getFeedingCurves(state),
        farm: state.location.farm,
        curve: selector(state, "curve"),
        curveDay: selector(state, "curveDay"),
        repetitionDate: selector(state, "repetitionDate"),
    };
};

export default compose(
    withTranslation(),
    connect(mapStateToProps, (dispatch) =>
        bindActionCreators({showModal}, dispatch)
    ),
    connectModal({name: ModalName}),
    reduxForm({
        form: ModalName,
        onSubmit: submit,
        validate,
        initialValues: {
            doseCorrection: 0,
            curveDay: 1,
            curveShift: 0,
            curve: undefined,
            punishment: 0,
        },
    })
)(FeedingManageFeedingModal);
